// Système d'optimisation et de mise en cache des images

interface ImageCacheEntry {
  url: string;
  timestamp: number;
  size: number;
}

class ImageOptimizer {
  private cache: Map<string, ImageCacheEntry> = new Map();
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours
  private readonly CACHE_NAME = 'image-cache-v2';
  private currentCacheSize = 0;

  constructor() {
    this.loadCacheFromStorage();
  }

  // Charger le cache depuis le Cache Storage
  private async loadCacheFromStorage() {
    try {
      if (!('caches' in window)) return;

      const cache = await caches.open(this.CACHE_NAME);
      const keys = await cache.keys();
      
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const timestamp = parseInt(response.headers.get('x-cache-timestamp') || '0');
          const size = parseInt(response.headers.get('content-length') || '0');
          
          // Vérifier si le cache est encore valide
          if (Date.now() - timestamp < this.CACHE_DURATION) {
            this.cache.set(request.url, {
              url: request.url,
              timestamp,
              size
            });
            this.currentCacheSize += size;
          } else {
            // Supprimer les entrées expirées
            await cache.delete(request);
          }
        }
      }
    } catch (error) {
      // Silently fail
    }
  }

  // Obtenir une image optimisée
  async getOptimizedImage(url: string): Promise<string> {
    // Vérifier si l'image est déjà en cache
    const cached = this.cache.get(url);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return url;
    }

    // Vérifier dans le Cache Storage
    try {
      if ('caches' in window) {
        const cache = await caches.open(this.CACHE_NAME);
        const response = await cache.match(url);
        if (response) {
          return url;
        }
      }
    } catch (error) {
      // Silently fail
    }

    // Télécharger et mettre en cache l'image
    return this.downloadAndCacheImage(url);
  }

  // Télécharger et mettre en cache une image
  private async downloadAndCacheImage(url: string): Promise<string> {
    try {
      const response = await fetch(url, {
        mode: 'cors',
        cache: 'force-cache'
      });
      
      if (!response.ok) {
        return url;
      }

      const blob = await response.blob();
      const timestamp = Date.now();
      const size = blob.size;

      // Vérifier la taille du cache
      if (this.currentCacheSize + size > this.MAX_CACHE_SIZE) {
        await this.cleanupCache(size);
      }

      // Stocker dans le Cache Storage
      if ('caches' in window) {
        const cache = await caches.open(this.CACHE_NAME);
        const headers = new Headers({
          'x-cache-timestamp': timestamp.toString(),
          'content-length': size.toString(),
          'content-type': blob.type
        });
        await cache.put(url, new Response(blob, { headers }));
      }

      // Mettre à jour le cache en mémoire
      this.cache.set(url, {
        url,
        timestamp,
        size
      });

      this.currentCacheSize += size;

      return url;
    } catch (error) {
      return url;
    }
  }

  // Nettoyer le cache pour libérer de l'espace
  private async cleanupCache(requiredSpace: number) {
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    if (!('caches' in window)) return;

    const cache = await caches.open(this.CACHE_NAME);
    let freedSpace = 0;

    for (const [url, entry] of entries) {
      if (this.currentCacheSize - freedSpace + requiredSpace <= this.MAX_CACHE_SIZE) {
        break;
      }

      await cache.delete(url);
      this.cache.delete(url);
      freedSpace += entry.size;
    }

    this.currentCacheSize -= freedSpace;
  }

  // Précharger des images
  async preloadImages(urls: string[]) {
    const promises = urls.map(url => 
      this.getOptimizedImage(url).catch(() => url)
    );
    await Promise.allSettled(promises);
  }

  // Obtenir les statistiques du cache
  getCacheStats() {
    return {
      entries: this.cache.size,
      size: this.currentCacheSize,
      maxSize: this.MAX_CACHE_SIZE,
      usage: (this.currentCacheSize / this.MAX_CACHE_SIZE * 100).toFixed(2) + '%'
    };
  }

  // Vider le cache
  async clearCache() {
    try {
      if ('caches' in window) {
        const cache = await caches.open(this.CACHE_NAME);
        const keys = await cache.keys();
        await Promise.all(keys.map(key => cache.delete(key)));
      }
      
      this.cache.clear();
      this.currentCacheSize = 0;
    } catch (error) {
      // Silently fail
    }
  }
}

export const imageOptimizer = new ImageOptimizer();